import { Profile, ReferralSound } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { Map } from 'immutable';

type ProfileUpdate = { state: string[]; value: string | object };

const referActions = ['hangup', 'loop'];

/**
 * The writes for an After Referral pick other than a number. The refer
 * action outranks the redirect, so a pick of Voicemail or of a parallel
 * call type clears it, or the call still hangs up or loops.
 */
export const getReferralTypeUpdates = (type: string): ProfileUpdate[] => {
    const isReferAction = referActions.includes(type);
    const updates: ProfileUpdate[] = [
        {
            state: ['callForwarding', 'unconditionalRedirect', 'type'],
            value: type === 'voicemail' ? 'voicemail' : 'busy',
        },
    ];

    if (type !== 'voicemail' && !isReferAction) {
        updates.push({
            state: ['parallelCalls', 'parallelCallType'],
            value: type,
        });
    }

    updates.push({
        state: ['spokenNotification', 'referActionType'],
        value: isReferAction ? type : 'noop',
    });

    return updates;
};

/** The writes for a picked forwarding number. */
export const getForwardingNumberUpdates = (number: object): ProfileUpdate[] => [
    {
        state: ['callForwarding', 'unconditionalRedirect', 'forwardingNumber'],
        value: number,
    },
    {
        state: ['callForwarding', 'unconditionalRedirect', 'type'],
        value: 'number',
    },
    {
        state: ['spokenNotification', 'referActionType'],
        value: 'noop',
    },
];

/**
 * A partial profile that sets the referral sound, or clears it when no
 * sound is given. Repeat sound needs a sound, so clearing the sound also
 * clears a stored loop. Hang up stays, as in the admin portal.
 */
export const buildReferralSoundUpdate = (
    profile: TypedMap<Profile>,
    referEntry?: TypedMap<ReferralSound>,
) => {
    const update = Map<string, unknown>()
        .set('key', profile.get('key'))
        .setIn(['spokenNotification', 'referEntry'], referEntry ?? Map());

    const clearsLoop =
        !referEntry &&
        profile.getIn(['spokenNotification', 'referActionType']) === 'loop';

    return clearsLoop
        ? update.setIn(['spokenNotification', 'referActionType'], 'noop')
        : update;
};
